import { el, VNode } from "@dhx/ts-common/dom";
import { EventSystem, IEventSystem } from "@dhx/ts-common/events";
import { locate } from "@dhx/ts-common/html";
import { KeyManager, IKeyManager } from "@dhx/ts-common/KeyManager";
import { IHandlers } from "@dhx/ts-common/types";

export enum EditorMode {
	editText = "text",
	selectItem = "select",
}

export enum EditorEvents {
	begin = "begin",
	end = "end",
}

export interface IEditorConfig {
	item?: any;
	mode: EditorMode;
	options?: string[];
}

export interface IEditor {
	config: IEditorConfig;
	events: IEventSystem<EditorEvents>;
	edit(targetId: string, config: IEditorConfig): VNode;
	isEditable(): boolean;
}

export class Editor implements IEditor {
	public config: IEditorConfig;
	public events: IEventSystem<EditorEvents>;

	private _item: any;
	private _handlers: IHandlers;
	private _currentValue: string;
	private _active: boolean;
	private _targetId: string;
	private _documentClick: (e) => void;
	private _keyManager: IKeyManager;

	constructor() {
		this.events = new EventSystem();
		this._keyManager = new KeyManager();
		this._documentClick = e => {
			const id = locate(e, "id");
			if (id !== "input_" + this._item.id) {
				this._removeClickListener();
				this._finishEdit();
			}
		};
		this._handlers = {
			editText: e => {
				this._currentValue = e.target.innerText;
			},
			itemSelected: e => {
				this._currentValue = e.target.value;
				this._finishEdit();
			},
			didInsert: (node: VNode) => {
				const element = node?.el;
				if (element) {
					const range = document.createRange();
					const selection = window.getSelection();

					range.setStart(element.childNodes[0], element.innerText.length);
					range.collapse(true);

					selection.removeAllRanges();
					selection.addRange(range);
					element.focus();
				}
			},
		};
	}

	edit(targetId: string, config: IEditorConfig) {
		if (this._active && this._item !== config.item) {
			this._finishEdit();
		}

		this._active = true;
		this._targetId = targetId;
		this.config = config;
		this._item = config.item;
		this._currentValue = this._item.value;

		this.events.fire(EditorEvents.begin, [targetId]);

		this._initOuterClick();
		this._addHotkeys();
		return this._draw();
	}
	isEditable(): boolean {
		return this._active;
	}
	private _draw() {
		if (this.config.mode === EditorMode.selectItem) {
			const opts = this.config.options;
			return el(
				"select",
				{
					id: "input_" + this._item.id,
					"data-dhx-id": this._item.id,
					onchange: this._handlers.itemSelected,
				},
				opts.map(item =>
					el(
						"option",
						{
							class: "editor-select",
							value: item,
							selected: this._currentValue === item,
							style: { border: "1px solid" },
						},
						item
					)
				)
			);
		} else {
			return el(
				"div.dhx_tree-editor",
				{
					_hooks: {
						didInsert: this._handlers.didInsert,
					},
					id: "input_" + this._item.id,
					oninput: this._handlers.editText,
					contentEditable: true,
				},
				this._currentValue
			);
		}
	}

	private _addHotkeys() {
		this._keyManager.addHotKey("escape", () => {
			this._finishEdit();
		});

		this._keyManager.addHotKey("enter", () => {
			this._finishEdit();
		});
	}

	private _removeHotkeys() {
		this._keyManager.removeHotKey();
	}

	private _finishEdit() {
		if (!this.events.fire(EditorEvents.end, [this._targetId, this._item.id, this._currentValue])) return;
		this._clear();
	}

	private _clear() {
		this._active = false;
		this._removeClickListener();
		this._removeHotkeys();
	}

	private _initOuterClick() {
		document.addEventListener("click", this._documentClick);
	}

	private _removeClickListener() {
		document.removeEventListener("click", this._documentClick);
	}
}
